import { useEffect, useState } from 'react';
import { SIDEBAR_MENU_BUTTON_SIZE } from '@/const';
import { AlertsList } from '@/components/shared/feedback/AlertsList';
import {
  useClearNotificationsMutation,
  useLazyGetNotificationsDMQuery,
  useLazyGetNotificationsQuery,
  useReadNotificationsMutation,
} from '@/store/api/notificationApi';
import { NotificationsBadge } from '@/components/shared/feedback/NotificationsBadge';
import { NotificationAlert } from '@/data/notifications';
import { useAuth } from '@/providers/AuthProvider';
import { useDispatch, useSelector } from 'react-redux';
import { setNotificationFeatureFlag } from '@/store/slices/applicationSlice';
import { notificationsFeatureFlagSelector } from '@/store/store';
import { useGetPreferencesQuery } from '@/store/api/userApi';
import { Skeleton } from '@/components/ui/skeleton';
import { BellIcon, TrashIcon } from '@/assets/Icons';
import { Icon } from '@/components/shared/display/Icon';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';

const Notifications = ({ defaultOpen = false }: { defaultOpen?: boolean }) => {
  const { user } = useAuth();
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const notificationsFeatureFlag = useSelector(notificationsFeatureFlagSelector);
  const isNotMobileResolution = window.matchMedia('(min-width: 1280px)').matches;
  const [fetchNotifications, { data: notifications, isLoading }] = useLazyGetNotificationsQuery({
    pollingInterval: 60000,
  });
  const [enabledNotifications, setEnabledNotifications] = useState([]);
  const [fetchDeliveryMethods, { data: deliveryMethods, isLoading: isLoadingDeliveryMethods }] =
    useLazyGetNotificationsDMQuery();
  const [clearNotifications, { isLoading: isClearing }] = useClearNotificationsMutation();
  const [readNotifications] = useReadNotificationsMutation();
  const { data: userPreferences } = useGetPreferencesQuery({});
  const [isDialogOpened, setDialogOpened] = useState<boolean>(false);

  const onAllNotificationsClear = async () => {
    await clearNotifications([...notificationsIds]);
    await fetchNotifications({});
  };

  useEffect(() => {
    if (userPreferences) {
      dispatch(setNotificationFeatureFlag(userPreferences?.notifications_enabled));
    }
  }, [userPreferences]);

  const notificationsIds = enabledNotifications?.map((item: NotificationAlert) => item.notification_id);
  const unreadNotifications = enabledNotifications?.filter((item: NotificationAlert) => !item.read);

  useEffect(() => {
    fetchDeliveryMethods({ uids: user?.uid });
    if (userPreferences?.notifications_enabled) {
      fetchNotifications({});
    }
  }, [isOpen, userPreferences]);

  const markAsRead = async () => {
    if (isOpen && notificationsIds?.length) {
      await readNotifications([...notificationsIds]);
      await fetchNotifications({});
    }
  };

  useEffect(() => {
    markAsRead();
  }, [isOpen, notifications]);

  useEffect(() => {
    if (deliveryMethods) {
      const enabledEventTypes = deliveryMethods
        .filter((item: any) => {
          const isDashboardEnabled = item?.delivery_methods.filter((deliveryMethod: any) => {
            return deliveryMethod.name.toLowerCase() === 'dashboard' && deliveryMethod.enabled;
          });

          return isDashboardEnabled?.length;
        })
        .map((item: any) => item?.event_type);

      const enabledNotificationsArray = notifications?.filter((item: NotificationAlert) =>
        enabledEventTypes.includes(item.event_type)
      );
      setEnabledNotifications(enabledNotificationsArray);
    }
  }, [isOpen, notifications, deliveryMethods]);

  return isLoading && isOpen ? (
    <Skeleton className="h-screen w-screen p-2" />
  ) : (
    <>
      <div className="flex flex-col gap-1 cursor-pointer relative" onClick={() => setIsOpen(true)}>
        <Button
          variant="ghost"
          size="lg"
          className={`h-8 rounded-2xl ${isOpen ? 'bg-[#12FFE2] text-[#021d3d]' : 'bg-transparent text-white'}`}
          aria-label="Alerts"
        >
          <Icon
            icon={BellIcon}
            className="mb-0.5 m-auto"
            style={{ width: SIDEBAR_MENU_BUTTON_SIZE, height: SIDEBAR_MENU_BUTTON_SIZE }}
          />
        </Button>
        <span className="text-[10px] leading-[14px] font-semibold">Alerts</span>

        {unreadNotifications?.length && notificationsFeatureFlag ? (
          <NotificationsBadge notifications={unreadNotifications} />
        ) : null}
      </div>

      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetContent
          side="left"
          className={`${isNotMobileResolution ? 'max-w-sm ml-[4.5rem]' : 'w-full ml-0'}`}
          style={isNotMobileResolution ? { marginLeft: '4.5rem' } : undefined}
        >
          <SheetHeader>
            <SheetTitle className="font-bold text-base">Alerts</SheetTitle>
          </SheetHeader>

          <div className="pl-0 pr-0 pt-0">
            {!isLoading && enabledNotifications ? (
              <div className="flex flex-col justify-between h-full items-start pb-8">
                <AlertsList notifications={enabledNotifications} />

                {notificationsIds?.length ? (
                  <div
                    className="w-full flex items-center justify-center gap-0 h-24 fixed bottom-0 bg-white"
                    style={{ boxShadow: '0 -2px 5px rgba(0, 0, 0, 0.1)' }}
                  >
                    {isDialogOpened ? (
                      <div className="px-0 py-0 text-center">
                        <div className="text-sm pb-[0.4375rem]">
                          Are you sure you want to delete all notifications?
                        </div>
                        <div className="flex items-center justify-center gap-2">
                          <Button size="sm" onClick={() => setDialogOpened(false)}>
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={async () => {
                              setDialogOpened(false);
                              await onAllNotificationsClear();
                            }}
                          >
                            Delete
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        className="font-medium uppercase text-red-600 border-red-600 hover:bg-red-50"
                        onClick={() => {
                          setDialogOpened(true);
                        }}
                        disabled={isClearing}
                      >
                        <Icon icon={TrashIcon} className="w-5 h-5 mr-2" />
                        Clear all
                      </Button>
                    )}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default Notifications;
